// src/components/admin/training/series/series-table.tsx
import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGetSeriesListQuery, useGetPackagesQuery } from "@/services/trainingApi";
import { useGetCoachesQuery } from "@/services/coachApi";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import {
  ClientTrainingSeries,
  TrainingSession,
  TrainingSessionStatus,
  TrainingType,
} from "@/types/training";
import { RescheduleModal } from "./reschedule-modal";
import { ScheduleModal } from "./schedule-modal";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Calendar, RefreshCw, Clock, MoreVertical, CheckCircle, Dumbbell } from "lucide-react";
import { format } from "date-fns";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableFilters from "@/components/ui/table/data-table-filter";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { displayDate, displayDateTime, formatClassDateTime, parseSessionDateTime } from "@/lib/helper";
import { cn } from "@/lib/utils";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_key: string;
  sort_order: string;
  search_key?: string;
  coach_id?: string;
  training_package_id?: string;
  facility_id?: string;
  training_type?: string;
}

const initialValue: SearchCriteriaType = {
  limit: 10,
  offset: 0,
  sort_key: "id",
  sort_order: "desc",
  coach_id: "all",
  training_package_id: "all",
  facility_id: "all",
  training_type: "all",
};


interface SeriesTableProps {
  memberId?: number;
}

export const SeriesTable: React.FC<SeriesTableProps> = ({ memberId }) => {
  const user = useSelector(
    (state: RootState) => state.auth.userInfo?.user
  );
  const orgId = user?.org_id || 0;
  const isSuperAdmin = user?.role_id === 1 || Boolean(user?.is_super_admin);
  const canReschedule = useMemo(() => {
    if (isSuperAdmin) return true;
    try {
      const accessLevels = JSON.parse(localStorage.getItem("accessLevels") || "{}");
      const lvl = (accessLevels?.reschedule_training || accessLevels?.training || accessLevels?.training_management)?.toString().toLowerCase();
      return lvl === "write" || lvl === "full_access" || lvl === "true";
    } catch {
      return false;
    }
  }, [isSuperAdmin]);

  // Search & Filter state
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteriaType>(initialValue);
  const [inputValue, setInputValue] = useState("");
  const [filterData, setFilterData] = useState<Record<string, any>>({});
  const [openFilter, setOpenFilter] = useState(false);

  // Selected series for drilldown sheet
  const [selectedSeries, setSelectedSeries] = useState<ClientTrainingSeries | null>(null);
  const [openSheet, setOpenSheet] = useState(false);

  // Modals
  const [rescheduleSession, setRescheduleSession] = useState<TrainingSession | null>(null);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);

  const [scheduleSeries, setScheduleSeries] = useState<ClientTrainingSeries | null>(null);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);

  const { data: seriesData, isLoading, refetch } = useGetSeriesListQuery({
    client_id: memberId || undefined,
    coach_id: searchCriteria.coach_id && searchCriteria.coach_id !== "all" ? Number(searchCriteria.coach_id) : undefined,
    training_package_id:
      searchCriteria.training_package_id && searchCriteria.training_package_id !== "all"
        ? Number(searchCriteria.training_package_id)
        : undefined,
    facility_id:
      searchCriteria.facility_id && searchCriteria.facility_id !== "all"
        ? Number(searchCriteria.facility_id)
        : undefined,
    training_type:
      searchCriteria.training_type && searchCriteria.training_type !== "all"
        ? searchCriteria.training_type
        : undefined,
    search: searchCriteria.search_key || undefined,
    limit: searchCriteria.limit,
    offset: searchCriteria.offset,
  });

  const { data: coachesData } = useGetCoachesQuery(
    { org_id: Number(orgId), query: "is_trainer=true&limit=100&sort_key=id&sort_order=desc" },
    { skip: !orgId }
  );

  const { data: packagesData } = useGetPackagesQuery(
    { status: "active", limit: 100 },
    { skip: !orgId }
  );

  const { data: facilitiesData } = useGetFacilitiesQuery(
    { orgId: Number(orgId), query: "status=active&limit=100" },
    { skip: !orgId }
  );

  const coachesList = useMemo(
    () => (Array.isArray(coachesData?.data) ? coachesData.data : []),
    [coachesData]
  );
  const packagesList = useMemo(
    () => (Array.isArray(packagesData?.data) ? packagesData.data : []),
    [packagesData]
  );
  const facilitiesList = useMemo(
    () => (Array.isArray(facilitiesData?.data) ? facilitiesData.data : []),
    [facilitiesData]
  );

  const rawSeriesList = useMemo(() => {
    return Array.isArray(seriesData?.data) ? seriesData.data : [];
  }, [seriesData]);

  // Client-side fallback filtering in case backend doesn't filter certain fields
  const seriesList = useMemo(() => {
    return rawSeriesList.filter((s) => {
      if (searchCriteria.facility_id && searchCriteria.facility_id !== "all") {
        if (String(s.facility_id) !== String(searchCriteria.facility_id)) return false;
      }
      if (searchCriteria.training_package_id && searchCriteria.training_package_id !== "all") {
        if (String(s.training_package_id) !== String(searchCriteria.training_package_id)) return false;
      }
      if (searchCriteria.training_type && searchCriteria.training_type !== "all") {
        const pkgType =
          s.training_type ||
          packagesList.find((p: any) => p.id === s.training_package_id)?.training_type;
        if (pkgType && pkgType !== searchCriteria.training_type) return false;
      }
      return true;
    });
  }, [
    rawSeriesList,
    searchCriteria.facility_id,
    searchCriteria.training_package_id,
    searchCriteria.training_type,
    packagesList,
  ]);

  const totalRecords =
    seriesData?.filtered_counts || seriesData?.total_counts || seriesList.length;

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleOpenSessions = (series: ClientTrainingSeries) => {
    setSelectedSeries(series);
    setOpenSheet(true);
  };

  const handleOpenReschedule = (session: TrainingSession) => {
    setRescheduleSession(session);
    setOpenRescheduleModal(true);
  };

  const handleOpenSchedule = (series: ClientTrainingSeries) => {
    setScheduleSeries(series);
    setOpenScheduleModal(true);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const filterDisplay = [
    {
      type: "select",
      name: "coach_id",
      label: "Trainer / Coach",
      options: [
        { id: "all", name: "All Coaches" },
        ...coachesList.map((c: any) => ({
          id: String(c.id),
          name: c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || `Coach #${c.id}`,
        })),
      ],
      function: (value: string) => handleFilterChange("coach_id", value),
    },
    {
      type: "select",
      name: "training_package_id",
      label: "Training Package",
      options: [
        { id: "all", name: "All Packages" },
        ...packagesList.map((p: any) => ({
          id: String(p.id),
          name: p.name,
        })),
      ],
      function: (value: string) => handleFilterChange("training_package_id", value),
    },
    {
      type: "select",
      name: "facility_id",
      label: "Facility / Studio",
      options: [
        { id: "all", name: "All Facilities" },
        ...facilitiesList.map((f: any) => ({
          id: String(f.id),
          name: f.name,
        })),
      ],
      function: (value: string) => handleFilterChange("facility_id", value),
    },
    {
      type: "select",
      name: "training_type",
      label: "Training Type",
      options: [
        { id: "all", name: "All Types" },
        { id: TrainingType.ONE_TO_ONE, name: "1-on-1 Personal Training" },
        { id: TrainingType.GROUP_PERSONAL, name: "Group Personal Training" },
        { id: TrainingType.GROUP_TRAINING, name: "Group Training" },
      ],
      function: (value: string) => handleFilterChange("training_type", value),
    },
  ];

  return (
    <div className="w-full space-y-4">
      {/* Top Header & Actions Matching Admin Standards */}
      <div className="flex flex-row items-center justify-between gap-2 px-3">
        <div className="flex-1 min-w-0">
          <TableSearchFilter
            setSearchCriteria={setSearchCriteria}
            inputValue={inputValue}
            setInputValue={setInputValue}
          />
        </div>

        <div className="flex flex-row items-center gap-2">
          {/* Filter Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`border rounded-md h-9 w-9 md:h-8 md:w-8 text-gray-400 flex items-center justify-center shrink-0 ${
                    Object.keys(filterData).length ? "border-primary" : ""
                  }`}
                  onClick={() => setOpenFilter(true)}
                >
                  <i
                    className={`fa fa-filter ${
                      Object.keys(filterData).length ? "text-primary" : ""
                    }`}
                  ></i>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Click to apply filter</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Desktop Series Listing Table */}
      <div className="hidden md:block rounded-none border border-border">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]">
            <TableHeader className="bg-secondary">
              <TableRow>
                <TableHead className="font-semibold">Series ID</TableHead>
                {!memberId && <TableHead className="font-semibold">Member</TableHead>}
                <TableHead className="font-semibold">Package Name</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Trainer</TableHead>
                <TableHead className="font-semibold">Facility</TableHead>
                <TableHead className="font-semibold">Credits Used</TableHead>
                <TableHead className="font-semibold">Sessions Progress</TableHead>
                <TableHead className="font-semibold">Schedule Dates</TableHead>
                <TableHead className="font-semibold">Validity Period</TableHead>
                <TableHead className="font-semibold">Created By</TableHead>
                <TableHead className="font-semibold">Created At</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={memberId ? 12 : 13} className="h-28 text-center">
                    <div className="flex space-x-2 justify-center items-center bg-white py-8">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : seriesList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={memberId ? 12 : 13} className="h-24 text-center text-sm text-gray-500">
                    No member training subscriptions found.
                  </TableCell>
                </TableRow>
              ) : (
                seriesList.map((series) => {
                  const scheduledSessions =
                    series.sessions?.filter(
                      (s) => s.status !== TrainingSessionStatus.PENDING_SCHEDULE
                    ).length || 0;
                  const pendingSessions =
                    Array.isArray(series.sessions) && series.sessions.length > 0
                      ? series.sessions.filter(
                          (s) => s.status === TrainingSessionStatus.PENDING_SCHEDULE
                        ).length
                      : Math.max(0, series.total_sessions - scheduledSessions);

                  const creditsUsed = Math.max(
                    0,
                    series.total_credits - (series.credits_remaining ?? series.total_credits)
                  );
                  const creditPercent = series.total_credits
                    ? Math.round((creditsUsed / series.total_credits) * 100)
                    : 0;

                  const pkgType =
                    series.training_type ||
                    packagesList.find((p: any) => p.id === series.training_package_id)?.training_type ||
                    TrainingType.ONE_TO_ONE;
                  const is1on1 = pkgType === TrainingType.ONE_TO_ONE;
                  const isGroupPersonal = pkgType === TrainingType.GROUP_PERSONAL;

                  return (
                    <TableRow
                      key={series.id}
                      className="cursor-pointer hover:bg-secondary transition-colors"
                      onClick={(e) => {
                        if (
                          e.target instanceof Element &&
                          !e.target.closest("button")
                        ) {
                          handleOpenSessions(series);
                        }
                      }}
                    >
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        #{series.id}
                      </TableCell>

                      {!memberId && (
                        <TableCell>
                          <div className="font-semibold text-sm">
                            {series.client_name || `Member #${series.client_id}`}
                          </div>
                          {series.client_email && (
                            <div className="text-xs text-muted-foreground">{series.client_email}</div>
                          )}
                        </TableCell>
                      )}

                      <TableCell className="text-sm font-medium">
                        <div>{series.package_name || "Personal Training"}</div>
                        {series.workout_name ? (
                          <Badge variant="outline" className="text-[10px] mt-1 bg-green-50 text-green-700 border-green-200">
                            Workout: {series.workout_name}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] mt-1 bg-gray-50 text-gray-500 border-gray-200">
                            No Workout
                          </Badge>
                        )}
                      </TableCell>

                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={
                            is1on1
                              ? "bg-blue-100 text-blue-800"
                              : isGroupPersonal
                              ? "bg-purple-100 text-purple-800"
                              : "bg-amber-100 text-amber-800"
                          }
                        >
                          {is1on1 ? "1-on-1 Personal" : isGroupPersonal ? "Group Personal" : "Group Training"}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-sm">
                        {series.coach_name || `Coach #${series.coach_id}`}
                      </TableCell>

                      <TableCell className="text-sm text-muted-foreground">
                        {series.facility_name || `Facility #${series.facility_id}`}
                      </TableCell>

                      <TableCell className="w-36">
                        <div className="flex justify-between text-xs font-semibold mb-1">
                          <span>{creditsUsed} / {series.total_credits}</span>
                          <span className={creditPercent === 100 ? "text-emerald-600 font-bold" : "text-muted-foreground"}>
                            {creditPercent}%
                          </span>
                        </div>
                        <Progress value={creditPercent} className="h-1.5 [&>div]:bg-emerald-500" />
                      </TableCell>

                      <TableCell>
                        <Badge variant="secondary" className="text-xs font-normal">
                          {series.sessions_completed} / {series.total_sessions} Done
                        </Badge>
                      </TableCell>

                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {series.schedule_start_date ? (
                          <span>{displayDate(series.schedule_start_date)} to {displayDate(series.schedule_end_date) || "N/A"}</span>
                        ) : (
                          <span className="text-gray-400 italic">Not scheduled</span>
                        )}
                      </TableCell>

                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {displayDate(series.valid_from) || "Sale Date"} to {displayDate(series.valid_until) || "N/A"}
                      </TableCell>

                      <TableCell className="text-xs text-muted-foreground">
                        {series.creator_name || series.created_by_name || "N/A"}
                      </TableCell>

                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {displayDateTime(series.created_at) || "N/A"}
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
                              >
                                <MoreVertical className="h-4 w-4" />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem
                                onClick={() => handleOpenSessions(series)}
                                className="cursor-pointer"
                              >
                                <Clock className="mr-2 h-4 w-4" />
                                View Classes
                              </DropdownMenuItem>
                              {pendingSessions > 0 && (
                                <DropdownMenuItem
                                  className="cursor-pointer text-blue-600 focus:text-blue-600 font-medium"
                                  onClick={() => handleOpenSchedule(series)}
                                >
                                  <Calendar className="mr-2 h-4 w-4" />
                                  Schedule Classes
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center py-10">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : seriesList.length > 0 ? (
          seriesList.map((series) => {
            const scheduledSessions =
              series.sessions?.filter(
                (s) => s.status !== TrainingSessionStatus.PENDING_SCHEDULE
              ).length || 0;
            const pendingSessions =
              Array.isArray(series.sessions) && series.sessions.length > 0
                ? series.sessions.filter(
                    (s) => s.status === TrainingSessionStatus.PENDING_SCHEDULE
                  ).length
                : Math.max(0, series.total_sessions - scheduledSessions);

            const pkgType =
              series.training_type ||
              packagesList.find((p: any) => p.id === series.training_package_id)?.training_type ||
              TrainingType.ONE_TO_ONE;
            const is1on1 = pkgType === TrainingType.ONE_TO_ONE;

            return (
              <div
                key={series.id}
                className="border border-border rounded-md p-3 space-y-2 bg-white cursor-pointer hover:bg-secondary/40 transition-colors"
                onClick={() => handleOpenSessions(series)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">
                      {series.client_name || `Member #${series.client_id}`}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {series.package_name || "Personal Training"}
                    </p>
                    {series.workout_name ? (
                      <Badge variant="outline" className="text-[10px] mt-0.5 bg-green-50 text-green-700 border-green-200">
                        Workout: {series.workout_name}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] mt-0.5 bg-gray-50 text-gray-500 border-gray-200">
                        No Workout
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="flex h-7 w-7 p-0 data-[state=open]:bg-muted"
                        >
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem
                          onClick={() => handleOpenSessions(series)}
                          className="cursor-pointer"
                        >
                          <Clock className="mr-2 h-4 w-4" />
                          View Classes
                        </DropdownMenuItem>
                        {pendingSessions > 0 && (
                          <DropdownMenuItem
                            className="cursor-pointer text-blue-600 focus:text-blue-600 font-medium"
                            onClick={() => handleOpenSchedule(series)}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            Schedule Classes
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <div className="text-muted-foreground">Type</div>
                  <div className="text-right">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {is1on1 ? "1-on-1" : "Group"}
                    </Badge>
                  </div>

                  <div className="text-muted-foreground">Trainer</div>
                  <div className="text-right font-medium">
                    {series.coach_name || `Coach #${series.coach_id}`}
                  </div>

                  <div className="text-muted-foreground">Facility</div>
                  <div className="text-right">
                    {series.facility_name || `Facility #${series.facility_id}`}
                  </div>

                  <div className="text-muted-foreground">Credits Remaining</div>
                  <div className="text-right font-semibold">
                    {series.credits_remaining} / {series.total_credits}
                  </div>

                  <div className="text-muted-foreground">Progress</div>
                  <div className="text-right">
                    {series.sessions_completed} / {series.total_sessions} completed
                  </div>

                  <div className="text-muted-foreground">Schedule Dates</div>
                  <div className="text-right">
                    {series.schedule_start_date ? (
                      `${displayDate(series.schedule_start_date)} to ${displayDate(series.schedule_end_date) || "N/A"}`
                    ) : (
                      <span className="text-gray-400 italic">Not scheduled</span>
                    )}
                  </div>

                  <div className="text-muted-foreground">Created By</div>
                  <div className="text-right">
                    {series.creator_name || series.created_by_name || "N/A"}
                  </div>

                  <div className="text-muted-foreground">Created At</div>
                  <div className="text-right">
                    {displayDateTime(series.created_at) || "N/A"}
                  </div>

                  <div className="text-muted-foreground">Valid Until</div>
                  <div className="text-right">
                    {displayDate(series.valid_until) || "N/A"}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">
            No member training subscriptions found.
          </div>
        )}
      </div>

      {/* Pagination */}
      {seriesList.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      {/* Filter Sheet */}
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      {/* Drilldown Sessions Sheet */}
      <Sheet open={openSheet} onOpenChange={setOpenSheet}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-lg font-bold">
              Class Schedule: {selectedSeries?.client_name || "Member"}
            </SheetTitle>
            <SheetDescription>
              {selectedSeries?.package_name} • {selectedSeries?.credits_remaining} credits remaining •{" "}
              {selectedSeries?.workout_name ? (
                <span className="text-emerald-600 font-medium">Workout: {selectedSeries.workout_name}</span>
              ) : (
                <span className="text-amber-600 font-medium">No Workout Assigned</span>
              )}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-3">
            <div className="flex justify-between items-center bg-muted/40 p-3 rounded-lg border text-xs">
              <div>
                <p className="font-semibold text-foreground">Trainer: {selectedSeries?.coach_name}</p>
                <p className="text-muted-foreground">Studio: {selectedSeries?.facility_name}</p>
              </div>
              {(() => {
                const pending =
                  Array.isArray(selectedSeries?.sessions) && selectedSeries.sessions.length > 0
                    ? selectedSeries.sessions.filter(
                        (s) => s.status === TrainingSessionStatus.PENDING_SCHEDULE
                      ).length
                    : 0;

                return pending > 0 ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-7 gap-1"
                    onClick={() => {
                      if (selectedSeries) handleOpenSchedule(selectedSeries);
                    }}
                  >
                    <Calendar className="size-3" />
                    Schedule ({pending})
                  </Button>
                ) : (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                    All Classes Scheduled
                  </Badge>
                );
              })()}
            </div>

            <div className="space-y-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                All Training Sessions
              </h3>

              {(!selectedSeries?.sessions || selectedSeries.sessions.length === 0) ? (
                <div className="text-center p-8 border rounded-lg bg-white text-muted-foreground text-xs">
                  No individual session slots booked yet. Click "Schedule" above to book recurring slots.
                </div>
              ) : (
                selectedSeries.sessions.map((session) => {
                  const isBooked = session.status === TrainingSessionStatus.BOOKED;
                  const isRescheduled = session.status === TrainingSessionStatus.RESCHEDULED;
                  const isCompleted = session.status === TrainingSessionStatus.COMPLETED;

                  // Dynamic cutoff indicator
                  const cutoffHours = (session as any)?.training_package?.reschedule_cutoff_hours ?? 48;
                  const sessionDate = parseSessionDateTime(session.start_time);
                  const hoursLeft = sessionDate
                    ? (sessionDate.getTime() - Date.now()) / (1000 * 60 * 60)
                    : 999;
                  const isPast = hoursLeft <= 0;
                  const isWithinCutoff = hoursLeft < cutoffHours && hoursLeft > 0;

                  // Start/End date and time calculation
                  const sessionStartDate = parseSessionDateTime(session.start_time);
                  const sessionEndDate = parseSessionDateTime(session.end_time);
                  const dateStr = sessionStartDate && !isNaN(sessionStartDate.getTime())
                    ? format(sessionStartDate, "dd-MMM-yyyy")
                    : (session.session_date ? format(new Date(session.session_date), "dd-MMM-yyyy") : null);
                  const startTimeStr = sessionStartDate && !isNaN(sessionStartDate.getTime())
                    ? format(sessionStartDate, "hh:mm a")
                    : null;
                  const endTimeStr = sessionEndDate && !isNaN(sessionEndDate.getTime())
                    ? format(sessionEndDate, "hh:mm a")
                    : null;
                  const timeRangeStr = startTimeStr
                    ? (endTimeStr ? `${startTimeStr} – ${endTimeStr}` : startTimeStr)
                    : null;

                  // Workout & exercises calculation
                  const workoutPlanName = session.workout_name || selectedSeries?.workout_name;
                  const workoutDayName = session.workout_day?.day_name;
                  const assignedExercises = (session.session_exercises && session.session_exercises.length > 0)
                    ? session.session_exercises
                    : (session.workout_day?.exercises || []);

                  return (
                    <div
                      key={session.id}
                      className="p-3.5 bg-white rounded-lg border shadow-sm space-y-2.5 transition-all hover:border-gray-300"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-bold text-sm">Class #{session.session_number}</span>
                            <Badge
                              variant="outline"
                              className={
                                isCompleted
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"
                                  : isBooked || isRescheduled
                                  ? "bg-blue-50 text-blue-700 border-blue-200 text-[10px]"
                                  : "bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
                              }
                            >
                              {session.status}
                            </Badge>
                            {isPast && (isBooked || isRescheduled) && (
                              <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-600 border-gray-200">
                                Past Session
                              </Badge>
                            )}
                            {isWithinCutoff && (
                              <Badge variant="destructive" className="text-[10px] bg-red-50 text-red-800 border-red-300">
                                &lt; {cutoffHours}h Cutoff
                              </Badge>
                            )}
                          </div>

                          {/* Date and Time Range */}
                          {dateStr ? (
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
                              <span className="flex items-center gap-1 font-medium text-foreground">
                                <Calendar className="size-3.5 text-primary" />
                                {dateStr}
                              </span>
                              {timeRangeStr && (
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Clock className="size-3.5 text-muted-foreground" />
                                  <span>{timeRangeStr}</span>
                                </span>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                              <Clock className="size-3 text-muted-foreground" />
                              Not scheduled
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 shrink-0">
                          {canReschedule && (isBooked || isRescheduled) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className={cn(
                                "text-xs h-8 gap-1 font-medium",
                                (isWithinCutoff || isPast)
                                  ? "text-amber-800 border-amber-200 bg-amber-50/50 hover:bg-amber-100"
                                  : "text-blue-700 border-blue-200 hover:bg-blue-50"
                              )}
                              onClick={() => handleOpenReschedule(session)}
                            >
                              <RefreshCw className="size-3" />
                              {isWithinCutoff || isPast ? "Locked" : "Reschedule"}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Workout & Exercises Section */}
                      <div className="pt-2 border-t border-dashed space-y-1.5">
                        <div className="flex items-center gap-1.5 text-xs">
                          <Dumbbell className="size-3.5 text-primary shrink-0" />
                          <span className="font-semibold text-foreground">
                            {workoutPlanName ? (
                              <>
                                <span>{workoutPlanName}</span>
                                {workoutDayName && (
                                  <span className="text-muted-foreground font-normal">
                                    {" "}• {workoutDayName}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-muted-foreground font-normal italic">
                                No workout assigned
                              </span>
                            )}
                          </span>
                        </div>

                        {assignedExercises && assignedExercises.length > 0 ? (
                          <div className="space-y-1 pl-5">
                            <span className="text-[11px] font-medium text-muted-foreground">
                              Exercises ({assignedExercises.length}):
                            </span>
                            <div className="flex flex-wrap gap-1.5 pt-0.5">
                              {assignedExercises.map((ex: any, idx: number) => {
                                const exName = ex.exercise_name || `Exercise ${idx + 1}`;
                                const setsCount = ex.sets;
                                const repsInfo = Array.isArray(ex.repetitions_per_set) && ex.repetitions_per_set.length > 0
                                  ? ex.repetitions_per_set[0]
                                  : ex.reps;
                                const details = setsCount
                                  ? `${setsCount} sets${repsInfo ? ` × ${repsInfo}` : ""}`
                                  : "";
                                return (
                                  <span
                                    key={ex.id || idx}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted/60 text-foreground text-[11px] border border-border"
                                  >
                                    <span className="font-medium">{exName}</span>
                                    {details && (
                                      <span className="text-muted-foreground text-[10px]">
                                        ({details})
                                      </span>
                                    )}
                                    {ex.is_completed && (
                                      <CheckCircle className="size-3 text-emerald-600 inline" />
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ) : workoutPlanName ? (
                          <p className="text-[11px] text-muted-foreground italic pl-5">
                            No specific exercises mapped for this day
                          </p>
                        ) : null}
                      </div>

                      {/* Coach notes and attendance */}
                      {(session.coach_notes || session.attendance_marked_at) && (
                        <div className="pt-1.5 border-t border-muted/50 space-y-1 text-[11px]">
                          {session.coach_notes && (
                            <p className="text-muted-foreground italic">
                              Note: {session.coach_notes}
                            </p>
                          )}
                          {session.attendance_marked_at && (
                            <p className="text-emerald-700 font-medium flex items-center gap-1">
                              <CheckCircle className="size-3" />
                              Attended{session.attendance_marked_by_name ? ` • Marked by ${session.attendance_marked_by_name}` : ""}{session.attendance_marked_at ? ` on ${displayDateTime(session.attendance_marked_at)}` : ""}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Reschedule Modal with 48h Cutoff Enforcement */}
      <RescheduleModal
        open={openRescheduleModal}
        onOpenChange={setOpenRescheduleModal}
        session={rescheduleSession}
        onSuccess={() => {
          refetch();
          setOpenSheet(false);
        }}
      />

      {/* Schedule Generation Wizard Modal */}
      <ScheduleModal
        open={openScheduleModal}
        onOpenChange={setOpenScheduleModal}
        series={scheduleSeries}
        onSuccess={() => {
          refetch();
          setOpenSheet(false);
        }}
      />
    </div>
  );
};